import { ApplicationContext } from '@shared/context/context';
import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import { createContext, useContext, useMemo, useRef } from 'react';

import { initializeMixpanelTracking } from './initialize-mixpanel-tracking';

import type { MixpanelClient, MixpanelTrackingService } from './initialize-mixpanel-tracking';
import type { TrackingEvent } from '@shared/tracking/tracking';
import type { ReactNode } from 'react';

export type MixpanelTrackingContextValue = {
    readonly trackEvent: (event: TrackingEvent) => void;
};

export const MixpanelTrackingContext = createContext<MixpanelTrackingContextValue | undefined>(undefined);

type MixpanelTrackingProviderProps = {
    readonly children: ReactNode;
    readonly client: MixpanelClient;
};

export const MixpanelTrackingProvider = ({ children, client }: MixpanelTrackingProviderProps): ReactNode => {
    const { applicationState } = useContext(ApplicationContext);
    const { customization, languageKey, myUserProfile } = applicationState;

    const serviceRef = useRef<MixpanelTrackingService | null>(null);

    const mixpanelProjectId = customization.mapOr(undefined, (c) => c.commonConfiguration.mixpanelProjectId);
    const tenantId = customization.map((c) => c.tenantId).unwrapOr('');
    const isLocalhost = globalThis.location?.hostname === 'localhost';

    const canInitialize =
        is.null(serviceRef.current) && !isLocalhost && is.nonEmptyString(mixpanelProjectId) && myUserProfile.isJust;

    if (canInitialize) {
        serviceRef.current = initializeMixpanelTracking(client, {
            languageKey,
            mixpanelProjectId,
            tenantId,
            user: myUserProfile.value,
        });
    }

    const isInitialized = !is.null(serviceRef.current);

    const value = useMemo<MixpanelTrackingContextValue>(() => {
        if (isInitialized && !is.null(serviceRef.current)) {
            return { trackEvent: serviceRef.current.sendTrackingEvent };
        }

        return { trackEvent: noop };
    }, [isInitialized]);

    return <MixpanelTrackingContext.Provider value={value}>{children}</MixpanelTrackingContext.Provider>;
};
